// Junk Football — /minigames hub
// 4 drives (2 offense, 2 defense), 4 plays per drive.
// LOS advances after each completion. Both teams pick formations.

import { useEffect, useRef, useState } from "react";
import MinigameStage from "./MinigameStage";
import FootballField from "./FootballField";

const W = 600, H = 600;
const LOS_Y = 480;
const END_ZONE_Y = 80;
const OL_BLOCK_MS = 1200;
const RUSHER_SPD = 118;
const WR_SPD = 138;
const CB_SPD = 78;
const CARRIER_SPD = 125;
const CARRIER_BOOST = 210;
const DEF_CHASE_SPD = 105;
const DEF_BOOST = 200;
const TACKLE_DIST = 15;
const CATCH_R = 24;
const INT_R = 16;
const BALL_DUR = 0.45;
const PAUSE_MS = 1350;
const BOOST_MS = 400;
const AI_THROW_MS_MIN = 650;
const AI_THROW_MS_VAR = 350;
const PLAYS_PER_DRIVE = 4;
const TOTAL_DRIVES = 4;

// Team palette — solid per side for clear distinction
const OFF_AMBER = "#fbbf24";  // player's offense
const OFF_OL    = "#92400e";  // OL (dark amber, same team less prominent)
const AI_RED    = "#f87171";  // AI (always red)
const DEF_BLUE  = "#38bdf8";  // player's defense

// --- Offense formation data (Y coords absolute, offset applied at init) ---
const MK = (role, sz, x, y, routes = []) => ({ role, sz, x, y, routes });

const FORMATIONS = [
  {
    id: "shotgun", label: "Shotgun", desc: "4 receivers spread wide",
    players: [
      MK("QB",  11, 300, LOS_Y + 32, []),
      MK("WR",   9,  50, LOS_Y -  8, [[50, 310], [50, 195]]),
      MK("WR",   9, 180, LOS_Y -  8, [[148, 308], [118, 238]]),
      MK("WR",   9, 420, LOS_Y -  8, [[452, 308], [482, 238]]),
      MK("WR",   9, 550, LOS_Y -  8, [[550, 310], [550, 195]]),
      MK("RB",   9, 362, LOS_Y + 20, [[460, 416], [500, 372]]),
      MK("OL",  11, 218, LOS_Y,      []),
      MK("OL",  11, 260, LOS_Y,      []),
      MK("OL",  11, 300, LOS_Y,      []),
      MK("OL",  11, 340, LOS_Y,      []),
      MK("OL",  11, 382, LOS_Y,      []),
    ],
  },
  {
    id: "iformation", label: "I-Form", desc: "Power run up the middle",
    players: [
      MK("QB",  11, 300, LOS_Y + 32, []),
      MK("WR",   9,  50, LOS_Y -  8, [[50, 305]]),
      MK("WR",   9, 550, LOS_Y -  8, [[550, 305]]),
      MK("TE",   9, 430, LOS_Y -  8, [[445, 338], [420, 258]]),
      MK("FB",   9, 300, LOS_Y + 15, [[300, 358], [300, 248]]),
      MK("RB",   9, 300, LOS_Y + 42, [[300, 308], [300, 220]]),
      MK("OL",  11, 200, LOS_Y,      []),
      MK("OL",  11, 250, LOS_Y,      []),
      MK("OL",  11, 300, LOS_Y,      []),
      MK("OL",  11, 350, LOS_Y,      []),
      MK("OL",  11, 400, LOS_Y,      []),
    ],
  },
  {
    id: "screen", label: "Screen", desc: "RB screen pass to the flat",
    players: [
      MK("QB",  11, 300, LOS_Y + 32, []),
      MK("WR",   9,  50, LOS_Y -  8, [[50, 200]]),
      MK("WR",   9, 160, LOS_Y -  8, [[100, 270]]),
      MK("WR",   9, 450, LOS_Y -  8, [[490, 270]]),
      MK("WR",   9, 550, LOS_Y -  8, [[550, 200]]),
      MK("RB",   9, 178, LOS_Y + 18, [[55, 420], [40, 338]]),
      MK("OL",  11, 218, LOS_Y,      []),
      MK("OL",  11, 260, LOS_Y,      []),
      MK("OL",  11, 300, LOS_Y,      []),
      MK("OL",  11, 340, LOS_Y,      []),
      MK("OL",  11, 382, LOS_Y,      []),
    ],
  },
  {
    id: "spread", label: "Spread", desc: "Crossing routes and wheels",
    players: [
      MK("QB",  11, 300, LOS_Y + 32, []),
      MK("WR",   9,  50, LOS_Y -  8, [[80, 305], [50, 205]]),
      MK("WR",   9, 200, LOS_Y -  8, [[228, 318], [265, 242]]),
      MK("WR",   9, 550, LOS_Y -  8, [[520, 305], [550, 205]]),
      MK("TE",   9, 418, LOS_Y -  8, [[365, 332], [308, 256]]),
      MK("RB",   9, 380, LOS_Y + 28, [[454, 400], [490, 360]]),
      MK("OL",  11, 235, LOS_Y,      []),
      MK("OL",  11, 270, LOS_Y,      []),
      MK("OL",  11, 300, LOS_Y,      []),
      MK("OL",  11, 330, LOS_Y,      []),
      MK("OL",  11, 365, LOS_Y,      []),
    ],
  },
];

// --- Defense formations ---
const DEFENSE_FORMATIONS = [
  { id: "passMan",  label: "Pass Man",   desc: "Pressed man-to-man, two deep safeties" },
  { id: "bear",     label: "Bear",       desc: "4-6 run-stop, six in the box" },
  { id: "highTwo",  label: "High 2",     desc: "Two deep safeties, zone underneath" },
  { id: "goalLine", label: "Goal Line",  desc: "Bunched zone, everyone near the line" },
];

function buildDefense(offFm, defFmId, isEnemy, currentLosY) {
  const losOffset = currentLosY - LOS_Y;
  const c = isEnemy ? AI_RED : DEF_BLUE;
  const skillIdxs = offFm.players
    .map((p, i) => i)
    .filter(i => ["WR", "TE"].includes(offFm.players[i].role));
  const w0 = offFm.players[skillIdxs[0]];
  const w1 = offFm.players[skillIdxs[1] ?? skillIdxs[0]];
  const dlY  = LOS_Y - 10 + losOffset;
  const lb1Y = LOS_Y - 44 + losOffset;
  const sY   = LOS_Y - 115 + losOffset;

  const makeDL  = (x)              => ({ role:"DL", color:c, sz:11, x, y:dlY });
  const makeLB  = (x, yo=0)        => ({ role:"LB", color:c, sz:10, x, y:lb1Y + yo });
  const makeS   = (x, yo=0)        => ({ role:"S",  color:c, sz:10, x, y:sY + yo });
  const makeCB  = (w, idx, yo=-18) => ({ role:"CB", color:c, sz:9, x:w.x, y:w.y+losOffset+yo, assignedWrIdx:idx });
  const makeZCB = (x, startYo, coverYo) => ({
    role:"CB", color:c, sz:9, x, y:LOS_Y+startYo+losOffset,
    assignedWrIdx:-1, coverageY:LOS_Y+coverYo+losOffset,
  });
  const makeBoxS = (x, yo) => ({ role:"S", color:c, sz:10, x, y:LOS_Y+yo+losOffset, coverageY:LOS_Y+yo+10+losOffset });

  // Pressed man-to-man, two deep safeties
  if (defFmId === "passMan") return [
    makeDL(228), makeDL(266), makeDL(334), makeDL(372),
    makeLB(260), makeLB(340),
    makeCB(w0, skillIdxs[0], -6),
    makeCB(w1, skillIdxs[1] ?? skillIdxs[0], -6),
    { role:"S", color:c, sz:10, x:300, y:LOS_Y-78+losOffset },
    makeS(185, -50), makeS(415, -50),
  ];

  // 4-6 Bear — six in the box, run-stop
  if (defFmId === "bear") return [
    makeDL(218), makeDL(268), makeDL(332), makeDL(382),
    { role:"LB", color:c, sz:11, x:162, y:dlY },
    { role:"LB", color:c, sz:11, x:438, y:dlY },
    makeLB(262, 22), makeLB(338, 22),
    makeCB(w0, skillIdxs[0], -12),
    makeCB(w1, skillIdxs[1] ?? skillIdxs[0], -12),
    makeBoxS(300, -34),
  ];

  // Cover 2 — two deep safeties, flat zones underneath
  if (defFmId === "highTwo") return [
    makeDL(228), makeDL(266), makeDL(334), makeDL(372),
    makeLB(240), makeLB(300), makeLB(360),
    makeZCB(105, -30, -68),
    makeZCB(495, -30, -68),
    makeS(188, -55), makeS(412, -55),
  ];

  // Goal line — bunched tight zone, everyone near LOS
  if (defFmId === "goalLine") return [
    makeDL(210), makeDL(266), makeDL(334), makeDL(390),
    makeLB(248, 22), makeLB(300, 22), makeLB(352, 22),
    makeZCB(128, -18, -26),
    makeZCB(472, -18, -26),
    makeBoxS(240, -28), makeBoxS(360, -28),
  ];

  // Fallback 4-3
  return [
    makeDL(228), makeDL(266), makeDL(334), makeDL(372),
    makeLB(240), makeLB(300), makeLB(360),
    makeCB(w0, skillIdxs[0]), makeCB(w1, skillIdxs[1] ?? skillIdxs[0]),
    makeS(220), makeS(380),
  ];
}

function moveToward(ent, tx, ty, spd, dt) {
  const dx = tx - ent.x, dy = ty - ent.y;
  const d = Math.hypot(dx, dy);
  if (d < 1) return;
  const step = Math.min(spd * dt, d);
  ent.x += (dx / d) * step;
  ent.y += (dy / d) * step;
}

// Mini SVG showing offense routes + basic defense for play-select cards
function FormationPreview({ fm }) {
  const defDots = buildDefense(fm, "passMan", true, LOS_Y);
  return (
    <svg viewBox="0 338 600 232" width="100%" height="52" style={{ display:"block" }}>
      <line x1="0" y1={LOS_Y} x2="600" y2={LOS_Y} stroke={OFF_AMBER} strokeWidth="1.5" strokeDasharray="8,6" opacity="0.45" />
      {fm.players.filter(p => p.routes?.length > 0).map((p, i) => {
        const pts = [[p.x, p.y], ...p.routes].map(([x, y]) => `${x},${y}`).join(" ");
        return <polyline key={i} points={pts} fill="none" stroke={OFF_AMBER} strokeWidth="1.8" strokeDasharray="5,4" opacity="0.55" />;
      })}
      {defDots.map((d, i) => <circle key={`d${i}`} cx={d.x} cy={d.y} r={d.sz * 0.72} fill={AI_RED} opacity="0.65" />)}
      {fm.players.map((p, i) => <circle key={`o${i}`} cx={p.x} cy={p.y} r={p.sz * 0.78} fill={p.role === "OL" ? OFF_OL : OFF_AMBER} />)}
    </svg>
  );
}

// Mini SVG showing defense alignment vs Shotgun for defense-select cards
function DefensePreview({ dfId }) {
  const offFm = FORMATIONS[0];
  const defenders = buildDefense(offFm, dfId, false, LOS_Y);
  return (
    <svg viewBox="0 338 600 232" width="100%" height="52" style={{ display:"block" }}>
      <line x1="0" y1={LOS_Y} x2="600" y2={LOS_Y} stroke={DEF_BLUE} strokeWidth="1.5" strokeDasharray="8,6" opacity="0.35" />
      {offFm.players.map((p, i) => <circle key={`o${i}`} cx={p.x} cy={p.y} r={p.sz * 0.72} fill={AI_RED} opacity="0.5" />)}
      {defenders.map((d, i) => d.role !== "DL" && (
        <circle key={`z${i}`} cx={d.x} cy={d.y}
          r={d.role === "S" ? 44 : d.role === "LB" ? 30 : 22}
          fill={DEF_BLUE} fillOpacity="0.09" stroke={DEF_BLUE} strokeOpacity="0.28" strokeWidth="1" />
      ))}
      {defenders.map((d, i) => <circle key={`d${i}`} cx={d.x} cy={d.y} r={d.sz * 0.78} fill={DEF_BLUE} />)}
    </svg>
  );
}

function Overlay({ children }) {
  return (
    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3
                    bg-slate-950/80 backdrop-blur-sm text-center px-5">
      {children}
    </div>
  );
}

function HUD({ playerScore, aiScore, driveNum, playNum, playerIsOffense }) {
  return (
    <div className="absolute top-2 left-0 right-0 z-20 flex items-center justify-between px-3 pointer-events-none">
      <div className="flex gap-1 items-center">
        <span className="text-[10px] font-bold text-white/60 uppercase tracking-wide">YOU</span>
        <span className="text-lg font-black text-amber-400 ml-1">{playerScore * 7}</span>
      </div>
      <div className="flex flex-col items-center">
        <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">
          Drive {driveNum + 1}/{TOTAL_DRIVES} · Play {playNum + 1}/{PLAYS_PER_DRIVE}
        </span>
        <span className="text-[9px] font-semibold text-white/40">
          {playerIsOffense ? "YOUR OFFENSE" : "YOUR DEFENSE"}
        </span>
      </div>
      <div className="flex gap-1 items-center">
        <span className="text-lg font-black text-red-400">{aiScore * 7}</span>
        <span className="text-[10px] font-bold text-white/60 uppercase tracking-wide ml-1">AI</span>
      </div>
    </div>
  );
}

function MsgFlash({ msg }) {
  return (
    <div className="absolute inset-0 z-[25] flex items-center justify-center pointer-events-none">
      <div className="bg-slate-900/90 border border-slate-600 rounded-xl px-6 py-3">
        <span className="text-xl font-black text-white">{msg}</span>
      </div>
    </div>
  );
}

export default function FootballMinigame() {
  const [phase, setPhase]                     = useState("pregame");
  const [driveNum, setDriveNum]               = useState(0);
  const [playNum, setPlayNum]                 = useState(0);
  const [playerScore, setPlayerScore]         = useState(0);
  const [aiScore, setAiScore]                 = useState(0);
  const [outcome, setOutcome]                 = useState(null);
  const [flashMsg, setFlashMsg]               = useState(null);
  const [formation, setFormation]             = useState(FORMATIONS[0]);
  const [losY, setLosY]                       = useState(LOS_Y);
  const [defFormation, setDefFormation]       = useState(DEFENSE_FORMATIONS[0]);
  const [defFormSelected, setDefFormSelected] = useState(false);
  const [displayRoutes, setDisplayRoutes]     = useState([]); // offense route lines shown in snap_ready
  const [displayZones, setDisplayZones]       = useState([]); // defense zone bubbles shown in defense_ready

  const phaseRef       = useRef("pregame");
  const driveNumRef    = useRef(0);
  const playNumRef     = useRef(0);
  const playerScoreRef = useRef(0);
  const aiScoreRef     = useRef(0);
  const playerIsOffRef = useRef(true);
  const losYRef        = useRef(LOS_Y);
  const aiFormRef      = useRef(FORMATIONS[0]);
  const stateRef       = useRef(null);

  const stageEl    = useRef(null);
  const offenseRefs = useRef([]);
  const defenseRefs = useRef([]);
  const ballRef    = useRef(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => {
    driveNumRef.current = driveNum;
    playerIsOffRef.current = driveNum % 2 === 0;
  }, [driveNum]);
  useEffect(() => { playNumRef.current = playNum; }, [playNum]);
  useEffect(() => { playerScoreRef.current = playerScore; }, [playerScore]);
  useEffect(() => { aiScoreRef.current = aiScore; }, [aiScore]);
  useEffect(() => { losYRef.current = losY; }, [losY]);

  const playerIsOffense = driveNum % 2 === 0;

  function initPlayState(fm, isOffense, currentLosY, defFmId = "43") {
    const losOffset  = currentLosY - LOS_Y;
    const offColor   = isOffense ? OFF_AMBER : AI_RED;
    const offOlColor = isOffense ? OFF_OL    : AI_RED;

    const offense = fm.players.map(p => ({
      ...p,
      color: p.role === "OL" ? offOlColor : offColor,
      y: p.y + losOffset,
      routes: p.routes.map(([rx, ry]) => [rx, Math.max(ry + losOffset, END_ZONE_Y + 15)]),
      routeIdx: 0,
    }));
    const defense = buildDefense(fm, defFmId, isOffense, currentLosY).map(d => ({
      ...d, boostUntil: 0,
    }));
    const qb = offense[0];
    stateRef.current = {
      offense, defense,
      ball: { active: false, x: qb.x, y: qb.y, t: 0, fromX: qb.x, fromY: qb.y, toX: 0, toY: 0, receiverIdx: -1 },
      snapTime: null, ballCarrierIdx: -1, ballCarrierTeam: "none",
      aiThrowTime: null, lastCarrierY: null,
    };
    offense.forEach((p, i) => {
      const el = offenseRefs.current[i];
      if (!el) return;
      el.style.backgroundColor = p.color;
      el.style.width  = p.sz + "px";
      el.style.height = p.sz + "px";
      el.style.borderRadius = "50%";
      el.style.opacity = "1";
    });
    defense.forEach((d, i) => {
      const el = defenseRefs.current[i];
      if (!el) return;
      el.style.backgroundColor = d.color;
      el.style.width  = d.sz + "px";
      el.style.height = d.sz + "px";
      el.style.borderRadius = "50%";
      el.style.opacity = "1";
    });
    if (ballRef.current) ballRef.current.style.opacity = "0";
  }

  function renderDots() {
    const s = stateRef.current;
    if (!s) return;
    s.offense.forEach((p, i) => {
      const el = offenseRefs.current[i];
      if (el) el.style.transform = `translate(${p.x - p.sz / 2}px, ${p.y - p.sz / 2}px)`;
    });
    s.defense.forEach((p, i) => {
      const el = defenseRefs.current[i];
      if (el) el.style.transform = `translate(${p.x - p.sz / 2}px, ${p.y - p.sz / 2}px)`;
    });
    const ball = s.ball;
    if (ballRef.current) {
      if (ball.active) {
        ballRef.current.style.opacity = "1";
        ballRef.current.style.transform = `translate(${ball.x - 5}px, ${ball.y - 5}px)`;
      } else {
        ballRef.current.style.opacity = "0";
      }
    }
  }

  function resolvePlay(newOutcome) {
    phaseRef.current = "play_over";
    setPhase("play_over");
    setOutcome(newOutcome);
    setFlashMsg({ td: "🏈 TOUCHDOWN!", tackle: "TACKLED", incomplete: "INCOMPLETE", interception: "INTERCEPTION!", sack: "SACK!" }[newOutcome] ?? "");
  }

  function updateOffenseRoutes(s, dt) {
    s.offense.forEach(p => {
      if (!p.routes?.length || p.role === "QB" || p.role === "OL") return;
      const idx = p.routeIdx ?? 0;
      if (idx >= p.routes.length) return;
      const [tx, ty] = p.routes[idx];
      const dx = tx - p.x, dy = ty - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 5) { p.routeIdx = idx + 1; return; }
      const spd = (p.role === "RB" || p.role === "FB") ? WR_SPD * 0.95 : WR_SPD;
      p.x += (dx / dist) * spd * dt;
      p.y += (dy / dist) * spd * dt;
    });
  }

  function updateRushers(s, dt, now, tx, ty) {
    if (now - (s.snapTime ?? now) < OL_BLOCK_MS) return;
    s.defense.forEach(d => { if (d.role === "DL") moveToward(d, tx, ty, RUSHER_SPD, dt); });
  }

  function updateCoverage(s, dt) {
    const curLos = losYRef.current;
    s.defense.forEach(d => {
      if (d.role === "CB" && d.assignedWrIdx != null && d.assignedWrIdx >= 0) {
        const wr = s.offense[d.assignedWrIdx];
        if (wr) moveToward(d, wr.x, wr.y - 16, CB_SPD, dt);
      } else if (d.role === "LB") {
        const qb = s.offense[0];
        moveToward(d, (qb.x + 300) / 2, (qb.y + curLos - 60) / 2, 55, dt);
      } else if (d.role === "S" || (d.role === "CB" && (d.assignedWrIdx == null || d.assignedWrIdx < 0))) {
        const qb = s.offense[0];
        const baseY = d.coverageY ?? (curLos - 130);
        moveToward(d, qb.x, Math.min(d.y, baseY), 40, dt);
      }
    });
  }

  function updateLivePass(s, dt, now) {
    const qb = s.offense[0];
    if (qb.y < losYRef.current + 62) qb.y += 28 * dt;
    updateOffenseRoutes(s, dt);
    updateRushers(s, dt, now, qb.x, qb.y);
    updateCoverage(s, dt);
    for (const d of s.defense) {
      if (d.role === "DL" && Math.hypot(d.x - qb.x, d.y - qb.y) < TACKLE_DIST) { resolvePlay("sack"); return; }
    }
  }

  function updateBallInAir(s, dt) {
    const ball = s.ball;
    ball.t += dt;
    const frac = Math.min(ball.t / BALL_DUR, 1);
    ball.x = ball.fromX + (ball.toX - ball.fromX) * frac;
    ball.y = ball.fromY + (ball.toY - ball.fromY) * frac - 55 * Math.sin(frac * Math.PI);
    s.defense.forEach(d => {
      if (d.role === "S" || d.role === "CB") moveToward(d, ball.toX, ball.toY, DEF_CHASE_SPD * 1.1, dt);
    });
    if (frac >= 1) {
      const receiver = s.offense[ball.receiverIdx];
      const recDist = receiver ? Math.hypot(ball.x - receiver.x, ball.y - receiver.y) : 999;
      const minDefDist = Math.min(...s.defense.map(d => Math.hypot(ball.x - d.x, ball.y - d.y)));
      ball.active = false;
      if (minDefDist < INT_R)     { resolvePlay("interception"); }
      else if (recDist < CATCH_R) { s.ballCarrierIdx = ball.receiverIdx; s.ballCarrierTeam = "offense"; phaseRef.current = "ball_carry"; setPhase("ball_carry"); }
      else                        { resolvePlay("incomplete"); }
    }
  }

  function updateBallCarry(s, dt) {
    const now = performance.now();
    const carrierArr = s.ballCarrierTeam === "offense" ? s.offense : s.defense;
    const carrier = carrierArr[s.ballCarrierIdx];
    if (!carrier) return;
    const boosted = now < (s.carrierBoostUntil ?? 0);
    moveToward(carrier, carrier.x + (300 - carrier.x) * 0.01, END_ZONE_Y - 15, boosted ? CARRIER_BOOST : CARRIER_SPD, dt);
    const chasers = s.ballCarrierTeam === "offense" ? s.defense : s.offense;
    chasers.forEach(d => { moveToward(d, carrier.x, carrier.y, now < (d.boostUntil ?? 0) ? DEF_BOOST : DEF_CHASE_SPD, dt); });
    if (carrier.y <= END_ZONE_Y) {
      if (s.ballCarrierTeam === "offense" && playerIsOffRef.current) setPlayerScore(sc => { playerScoreRef.current = sc + 1; return sc + 1; });
      else if (s.ballCarrierTeam === "offense")                      setAiScore(sc => { aiScoreRef.current = sc + 1; return sc + 1; });
      resolvePlay("td");
      return;
    }
    for (const d of chasers) {
      if (Math.hypot(d.x - carrier.x, d.y - carrier.y) < TACKLE_DIST) {
        s.lastCarrierY = carrier.y;
        resolvePlay("tackle");
        return;
      }
    }
  }

  function updateDefenseLive(s, dt, now) {
    const qb = s.offense[0];
    if (qb.y < losYRef.current + 62) qb.y += 26 * dt;
    updateOffenseRoutes(s, dt);
    updateRushers(s, dt, now, qb.x, qb.y);
    updateCoverage(s, dt);
    s.defense.forEach(d => {
      if (now < (d.boostUntil ?? 0) && d.role !== "DL") {
        const tgt = s.ballCarrierIdx >= 0 ? s.offense[s.ballCarrierIdx] : qb;
        if (tgt) moveToward(d, tgt.x, tgt.y, DEF_BOOST - DEF_CHASE_SPD, dt);
      }
    });
    for (const d of s.defense) {
      if (d.role === "DL" && Math.hypot(d.x - qb.x, d.y - qb.y) < TACKLE_DIST) { resolvePlay("sack"); return; }
    }
    const closestRusher = Math.min(...s.defense.filter(d => d.role === "DL").map(d => Math.hypot(d.x - qb.x, d.y - qb.y)));
    if (closestRusher < 55 && s.aiThrowTime != null) s.aiThrowTime = now;
    if (s.aiThrowTime != null && now >= s.aiThrowTime && !s.ball.active) {
      s.aiThrowTime = null;
      let bestIdx = -1, bestDist = -1;
      s.offense.forEach((p, i) => {
        if (!["WR","TE","RB","FB"].includes(p.role)) return;
        const minDef = Math.min(...s.defense.map(d => Math.hypot(d.x - p.x, d.y - p.y)));
        if (minDef > bestDist) { bestDist = minDef; bestIdx = i; }
      });
      if (bestIdx >= 0) {
        const recv = s.offense[bestIdx];
        Object.assign(s.ball, { active: true, fromX: qb.x, fromY: qb.y, toX: recv.x, toY: recv.y, t: 0, receiverIdx: bestIdx });
        phaseRef.current = "ball_in_air";
        setPhase("ball_in_air");
      }
    }
  }

  const isLivePhase = ["live_pass", "ball_in_air", "ball_carry", "defense_live"].includes(phase);
  useEffect(() => {
    if (!isLivePhase) return;
    let lastTime = null, id;
    const frame = (now) => {
      const dt = lastTime != null ? Math.min((now - lastTime) / 1000, 0.05) : 0;
      lastTime = now;
      const p = phaseRef.current, s = stateRef.current;
      if (s) {
        if      (p === "live_pass")    updateLivePass(s, dt, now);
        else if (p === "ball_in_air")  updateBallInAir(s, dt);
        else if (p === "ball_carry")   updateBallCarry(s, dt);
        else if (p === "defense_live") updateDefenseLive(s, dt, now);
        renderDots();
      }
      id = requestAnimationFrame(frame);
    };
    id = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(id);
  }, [isLivePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === "snap_ready" || phase === "defense_ready") renderDots();
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== "play_over") return;
    const capturedOutcome = outcome;
    const t = setTimeout(() => {
      const drive = driveNumRef.current;
      const play  = playNumRef.current;
      const isOff = playerIsOffRef.current;
      const driveOver = capturedOutcome === "td" || capturedOutcome === "interception" || play >= PLAYS_PER_DRIVE - 1;

      setFlashMsg(null);
      setOutcome(null);

      if (driveOver) {
        const nextDrive = drive + 1;
        setLosY(LOS_Y); losYRef.current = LOS_Y;
        if (nextDrive >= TOTAL_DRIVES) {
          setPhase("game_over"); phaseRef.current = "game_over";
        } else {
          setDriveNum(nextDrive); driveNumRef.current = nextDrive;
          playerIsOffRef.current = nextDrive % 2 === 0;
          setPlayNum(0); playNumRef.current = 0;
          if (nextDrive % 2 === 0) { setPhase("formation_select"); phaseRef.current = "formation_select"; }
          else { startDefenseDrive(nextDrive); }
        }
      } else {
        // Advance LOS to where carrier was tackled
        if (capturedOutcome === "tackle") {
          const lastY = stateRef.current?.lastCarrierY;
          if (lastY != null) {
            const newLos = Math.max(END_ZONE_Y + 60, Math.round(lastY));
            setLosY(newLos); losYRef.current = newLos;
          }
        }
        const nextPlay = play + 1;
        setPlayNum(nextPlay); playNumRef.current = nextPlay;
        if (isOff) { setPhase("formation_select"); phaseRef.current = "formation_select"; }
        else { startDefenseDrive(drive); }
      }
    }, PAUSE_MS);
    return () => clearTimeout(t);
  }, [phase, outcome]); // eslint-disable-line react-hooks/exhaustive-deps

  function startDefenseDrive(drive) {
    const aiFm = FORMATIONS[drive % FORMATIONS.length];
    aiFormRef.current = aiFm;
    offenseRefs.current.forEach(el => { if (el) el.style.opacity = "0"; });
    defenseRefs.current.forEach(el => { if (el) el.style.opacity = "0"; });
    setDefFormSelected(false);
    setPhase("defense_ready"); phaseRef.current = "defense_ready";
  }

  function handleDefFormationSelect(df) {
    setDefFormation(df);
    initPlayState(aiFormRef.current, false, losYRef.current, df.id);
    // Build zone display from freshly-initialized defense positions
    const s = stateRef.current;
    const zones = s.defense.map(d => {
      const assignedWr = (d.assignedWrIdx ?? -1) >= 0 ? s.offense[d.assignedWrIdx] : null;
      return {
        x: d.x, y: d.y, role: d.role,
        r: d.role === "DL" ? 14 : d.role === "CB" ? 28 : d.role === "LB" ? 44 : 62,
        assignedX: assignedWr?.x ?? null, assignedY: assignedWr?.y ?? null,
      };
    });
    setDisplayZones(zones);
    setDefFormSelected(true);
  }

  function beginDefensePlay() {
    const s = stateRef.current;
    if (!s) return;
    setDisplayZones([]);
    s.snapTime = performance.now();
    s.aiThrowTime = s.snapTime + AI_THROW_MS_MIN + Math.random() * AI_THROW_MS_VAR;
    phaseRef.current = "defense_live"; setPhase("defense_live");
  }

  function handleFormationSelect(fm) {
    const aiDefIds = ["43", "34", "nickel", "blitz"];
    const aiDefId = aiDefIds[Math.floor(Math.random() * aiDefIds.length)];
    setFormation(fm);
    initPlayState(fm, true, losYRef.current, aiDefId);
    // Build route display from the freshly-initialized offense positions
    const routes = stateRef.current.offense
      .filter(p => p.routes?.length > 0)
      .map(p => ({ sx: p.x, sy: p.y, waypoints: p.routes }));
    setDisplayRoutes(routes);
    setPhase("snap_ready"); phaseRef.current = "snap_ready";
  }

  function handleHike() {
    const s = stateRef.current;
    if (!s) return;
    setDisplayRoutes([]);
    s.snapTime = performance.now();
    phaseRef.current = "live_pass"; setPhase("live_pass");
  }

  function handleStagePointer(clientX, clientY) {
    if (!stageEl.current || !stateRef.current) return;
    const rect = stageEl.current.getBoundingClientRect();
    const sx = (clientX - rect.left) * (W / rect.width);
    const sy = (clientY - rect.top)  * (H / rect.height);
    const p = phaseRef.current, s = stateRef.current;

    if (p === "live_pass") {
      let bestIdx = -1, bestDist = Infinity;
      s.offense.forEach((pl, i) => {
        if (!["WR","TE","RB","FB"].includes(pl.role)) return;
        const d = Math.hypot(sx - pl.x, sy - pl.y);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      });
      if (bestIdx < 0) return;
      const recv = s.offense[bestIdx];
      Object.assign(s.ball, { active: true, fromX: s.offense[0].x, fromY: s.offense[0].y, toX: recv.x, toY: recv.y, t: 0, receiverIdx: bestIdx });
      phaseRef.current = "ball_in_air"; setPhase("ball_in_air");
    } else if (p === "ball_carry" && playerIsOffRef.current) {
      if (s.ballCarrierIdx >= 0) s.carrierBoostUntil = performance.now() + BOOST_MS;
    } else if (p === "ball_in_air" && playerIsOffRef.current) {
      s.carrierBoostUntil = performance.now() + BOOST_MS;
    } else if ((p === "ball_carry" && !playerIsOffRef.current) || p === "defense_live") {
      boostNearestDefenders(s, sx, sy);
    }
  }

  function boostNearestDefenders(s, sx, sy) {
    const now = performance.now();
    [...s.defense.filter(d => d.role !== "DL")]
      .sort((a, b) => Math.hypot(sx - a.x, sy - a.y) - Math.hypot(sx - b.x, sy - b.y))
      .slice(0, 2)
      .forEach(d => { d.boostUntil = now + BOOST_MS; });
  }

  function resetGame() {
    setPhase("pregame"); phaseRef.current = "pregame";
    setDriveNum(0); driveNumRef.current = 0;
    setPlayNum(0);  playNumRef.current = 0;
    setPlayerScore(0); playerScoreRef.current = 0;
    setAiScore(0);     aiScoreRef.current = 0;
    setLosY(LOS_Y);    losYRef.current = LOS_Y;
    setOutcome(null); setFlashMsg(null); setDefFormSelected(false);
    setDisplayRoutes([]); setDisplayZones([]);
    playerIsOffRef.current = true; stateRef.current = null;
    offenseRefs.current.forEach(el => { if (el) el.style.opacity = "0"; });
    defenseRefs.current.forEach(el => { if (el) el.style.opacity = "0"; });
  }

  return (
    <MinigameStage designW={W} designH={H}>
      <div
        ref={stageEl}
        className="relative w-full h-full overflow-hidden select-none"
        onMouseDown={e => handleStagePointer(e.clientX, e.clientY)}
        onTouchStart={e => { e.preventDefault(); const t = e.touches[0]; handleStagePointer(t.clientX, t.clientY); }}
      >
        <FootballField losY={losY} endZoneY={END_ZONE_Y} />

        {!["pregame","game_over"].includes(phase) && (
          <HUD playerScore={playerScore} aiScore={aiScore} driveNum={driveNum} playNum={playNum} playerIsOffense={playerIsOffense} />
        )}

        {Array.from({ length: 11 }).map((_, i) => (
          <div key={`off-${i}`} ref={el => { offenseRefs.current[i] = el; }}
            className="absolute pointer-events-none"
            style={{ top: 0, left: 0, width: 10, height: 10, borderRadius: "50%", backgroundColor: OFF_AMBER, opacity: 0 }} />
        ))}
        {Array.from({ length: 11 }).map((_, i) => (
          <div key={`def-${i}`} ref={el => { defenseRefs.current[i] = el; }}
            className="absolute pointer-events-none"
            style={{ top: 0, left: 0, width: 10, height: 10, borderRadius: "50%", backgroundColor: AI_RED, opacity: 0 }} />
        ))}
        <div ref={ballRef} className="absolute pointer-events-none"
          style={{ top: 0, left: 0, width: 10, height: 10, borderRadius: "50%", backgroundColor: "#f59e0b", border: "2px solid #92400e", opacity: 0, zIndex: 10 }} />

        {flashMsg && <MsgFlash msg={flashMsg} />}

        {/* Receiver route lines — visible during snap_ready */}
        {phase === "snap_ready" && displayRoutes.length > 0 && (
          <svg className="absolute inset-0 pointer-events-none z-[15]"
               style={{ width: "100%", height: "100%" }} viewBox="0 0 600 600">
            {displayRoutes.map((route, i) => {
              const pts = [[route.sx, route.sy], ...route.waypoints];
              const d = pts.map(([x, y], j) => `${j === 0 ? "M" : "L"}${x},${y}`).join(" ");
              return (
                <g key={i}>
                  <path d={d} fill="none" stroke={OFF_AMBER} strokeWidth="1.8"
                        strokeDasharray="5,4" opacity="0.55" />
                  {route.waypoints.map(([wx, wy], j) => (
                    <circle key={j} cx={wx} cy={wy} r={3.5} fill={OFF_AMBER} opacity="0.75" />
                  ))}
                </g>
              );
            })}
          </svg>
        )}

        {/* Defense zone bubbles — visible in defense_ready after formation selected */}
        {phase === "defense_ready" && defFormSelected && displayZones.length > 0 && (
          <svg className="absolute inset-0 pointer-events-none z-[15]"
               style={{ width: "100%", height: "100%" }} viewBox="0 0 600 600">
            {/* CB assignment lines to their WR */}
            {displayZones.filter(z => z.role === "CB" && z.assignedX != null).map((z, i) => (
              <line key={`cl-${i}`} x1={z.x} y1={z.y} x2={z.assignedX} y2={z.assignedY}
                    stroke={DEF_BLUE} strokeWidth="1.2" strokeDasharray="4,3" opacity="0.35" />
            ))}
            {/* Zone circles per defender */}
            {displayZones.map((z, i) => (
              <circle key={`cz-${i}`} cx={z.x} cy={z.y} r={z.r}
                      fill={DEF_BLUE} fillOpacity="0.1"
                      stroke={DEF_BLUE} strokeOpacity="0.38" strokeWidth="1.5" />
            ))}
          </svg>
        )}

        {phase === "pregame" && (
          <Overlay>
            <div className="text-4xl">🏈</div>
            <h3 className="text-2xl font-black text-white">Junk Football</h3>
            <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
              2 offense drives · 2 defense drives · 4 plays each.<br />
              <span className="text-amber-400 font-semibold">Offense:</span> tap field to throw to nearest receiver.<br />
              <span className="text-sky-400 font-semibold">Defense:</span> tap to boost your defenders.
            </p>
            <button onClick={() => { setPhase("formation_select"); phaseRef.current = "formation_select"; }}
              className="mt-2 px-7 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm transition-colors">
              KICK OFF
            </button>
          </Overlay>
        )}

        {phase === "formation_select" && (
          <Overlay>
            <p className="text-xs font-bold text-amber-400 uppercase tracking-widest">Pick a Formation</p>
            <p className="text-[10px] text-slate-500">Play {playNum + 1} of {PLAYS_PER_DRIVE}</p>
            <div className="grid grid-cols-2 gap-2 mt-1 w-full max-w-xs">
              {FORMATIONS.map(fm => (
                <button key={fm.id} onClick={() => handleFormationSelect(fm)}
                  className="flex flex-col items-start px-2 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-amber-500 transition-colors text-left overflow-hidden">
                  <FormationPreview fm={fm} />
                  <span className="text-sm font-black text-white mt-1">{fm.label}</span>
                  <span className="text-[10px] text-slate-400 leading-tight">{fm.desc}</span>
                </button>
              ))}
            </div>
          </Overlay>
        )}

        {phase === "snap_ready" && (
          <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center">
            <div className="bg-slate-950/85 backdrop-blur-sm rounded-2xl px-6 py-3 flex flex-col items-center gap-2 border border-slate-700/50 shadow-xl">
              <p className="text-[10px] text-white/50 font-semibold uppercase tracking-widest">
                Tap field to throw · tap near RB to dump off
              </p>
              <button onClick={handleHike}
                className="px-8 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-base transition-colors shadow-lg">
                HIKE!
              </button>
            </div>
          </div>
        )}

        {phase === "defense_ready" && !defFormSelected && (
          <Overlay>
            <p className="text-xs font-bold text-sky-400 uppercase tracking-widest">Pick Your Defense</p>
            <p className="text-[10px] text-slate-500">Play {playNum + 1} of {PLAYS_PER_DRIVE}</p>
            <div className="grid grid-cols-2 gap-2 mt-1 w-full max-w-xs">
              {DEFENSE_FORMATIONS.map(df => (
                <button key={df.id} onClick={() => handleDefFormationSelect(df)}
                  className="flex flex-col items-start px-2 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-sky-500 transition-colors text-left overflow-hidden">
                  <DefensePreview dfId={df.id} />
                  <span className="text-sm font-black text-white mt-1">{df.label}</span>
                  <span className="text-[10px] text-slate-400 leading-tight">{df.desc}</span>
                </button>
              ))}
            </div>
          </Overlay>
        )}

        {phase === "defense_ready" && defFormSelected && (
          <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center">
            <div className="bg-slate-950/85 backdrop-blur-sm rounded-2xl px-6 py-3 flex flex-col items-center gap-2 border border-slate-700/50 shadow-xl">
              <p className="text-[11px] font-black text-sky-400 uppercase tracking-widest">
                Tap to boost <span style={{ color: DEF_BLUE }}>blue defenders</span> toward the carrier
              </p>
              <button onClick={beginDefensePlay}
                className="px-8 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-black text-base transition-colors shadow-lg">
                READY!
              </button>
            </div>
          </div>
        )}

        {phase === "defense_live" && (
          <div className="absolute bottom-6 left-0 right-0 z-20 flex justify-center pointer-events-none">
            <span className="text-[10px] text-sky-400/70 font-semibold uppercase tracking-widest">Tap to boost defenders</span>
          </div>
        )}

        {phase === "ball_carry" && playerIsOffense && (
          <div className="absolute bottom-6 left-0 right-0 z-20 flex justify-center pointer-events-none">
            <span className="text-[10px] text-amber-400/70 font-semibold uppercase tracking-widest">Tap to boost runner!</span>
          </div>
        )}

        {phase === "game_over" && (
          <Overlay>
            <div className="text-3xl">{playerScore > aiScore ? "🏆" : playerScore < aiScore ? "😔" : "🤝"}</div>
            <h3 className="text-2xl font-black text-white">
              {playerScore > aiScore ? "YOU WIN!" : playerScore < aiScore ? "AI WINS" : "TIED!"}
            </h3>
            <div className="flex gap-6 text-center">
              <div>
                <div className="text-2xl font-black text-amber-400">{playerScore * 7}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">You</div>
              </div>
              <div className="text-slate-600 text-xl font-bold self-center">–</div>
              <div>
                <div className="text-2xl font-black text-red-400">{aiScore * 7}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">AI</div>
              </div>
            </div>
            <button onClick={resetGame}
              className="mt-2 px-7 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm transition-colors">
              Play Again
            </button>
          </Overlay>
        )}
      </div>
    </MinigameStage>
  );
}
